// =============================================================================
// EnviarWhatsAppModal - escolhe COMO enviar a mensagem de WhatsApp da operação.
// =============================================================================
// Aberto pelo botão "Enviar análise de crédito" (aba Mensagens). Oferece 3 modos:
//   a) Template     - usar um template salvo
//   b) Novo template - definir um template novo, salvar e usar
//   c) Personalizada - escrever uma mensagem avulsa (não salva)
// O envio é por deep-link do WhatsApp (abre o WhatsApp do operador). Variáveis
// suportadas no corpo: {{nome}} (comprador) e {{link}} (formulário de crédito).
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import type { Operacao, TemplateWhatsapp } from '../types';
import { templatesWhatsappApi } from '../services/api';
import {
  linkFormularioCredito,
  textoAnaliseCredito,
  resolverVariaveisMensagem,
  montarLinkWhatsApp,
} from '../utils/whatsappAnaliseCredito';

type Modo = 'template' | 'novo' | 'custom';

interface Props {
  op: Operacao;
  onClose: () => void;
}

export default function EnviarWhatsAppModal({ op, onClose }: Props) {
  const principal = useMemo(
    () => op.compradores.find((c) => c.posicaoIsolve === 1) ?? op.compradores[0],
    [op.compradores],
  );
  const idCstimob = op.idCstimob ?? '';
  const link = idCstimob !== '' ? linkFormularioCredito(idCstimob) : '';
  const nomeComprador = principal?.nome ?? '';

  const [modo, setModo] = useState<Modo>('template');
  const [templates, setTemplates] = useState<TemplateWhatsapp[]>([]);
  const [carregandoTpl, setCarregandoTpl] = useState(true);
  const [selId, setSelId] = useState<string>('');
  const [novoNome, setNovoNome] = useState('');
  const [novoCorpo, setNovoCorpo] = useState('Olá {{nome}}! Segue o link para a sua análise de crédito: {{link}}');
  const [customText, setCustomText] = useState(idCstimob !== '' ? textoAnaliseCredito(idCstimob) : 'Olá {{nome}}!');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    templatesWhatsappApi
      .listar()
      .then((ts) => {
        setTemplates(ts);
        if (ts.length) setSelId(ts[0].id);
        else setModo('custom'); // sem templates → começa na personalizada
      })
      .catch(() => setTemplates([]))
      .finally(() => setCarregandoTpl(false));
  }, []);

  const semTelefone = !principal?.telefone;

  const resolver = (corpo: string) => resolverVariaveisMensagem(corpo, { nome: nomeComprador, link });

  // Pré-visualização do texto final conforme o modo
  const preview = useMemo(() => {
    if (modo === 'template') {
      const t = templates.find((x) => x.id === selId);
      return t ? resolver(t.corpo) : '';
    }
    if (modo === 'novo') return resolver(novoCorpo);
    return resolver(customText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, selId, templates, novoCorpo, customText, nomeComprador, link]);

  const dispararWhatsApp = (texto: string) => {
    window.open(montarLinkWhatsApp(principal!.telefone!, texto), '_blank', 'noopener');
    onClose();
  };

  const enviar = async () => {
    setErro(null);
    if (semTelefone) { setErro('Proponente principal sem telefone cadastrado.'); return; }

    if (modo === 'template') {
      const t = templates.find((x) => x.id === selId);
      if (!t) { setErro('Selecione um template.'); return; }
      dispararWhatsApp(resolver(t.corpo));
      return;
    }

    if (modo === 'novo') {
      if (!novoNome.trim() || !novoCorpo.trim()) { setErro('Informe nome e corpo do template.'); return; }
      setSalvando(true);
      try {
        const criado = await templatesWhatsappApi.criar(novoNome.trim(), novoCorpo.trim());
        setTemplates((prev) => [...prev, criado].sort((a, b) => a.nome.localeCompare(b.nome)));
        dispararWhatsApp(resolver(criado.corpo));
      } catch (e: any) {
        setErro(e?.response?.data?.erro ?? 'Não foi possível salvar o template.');
      } finally {
        setSalvando(false);
      }
      return;
    }

    // custom
    if (!customText.trim()) { setErro('Escreva a mensagem.'); return; }
    dispararWhatsApp(resolver(customText));
  };

  const TabBtn = ({ value, children }: { value: Modo; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={() => { setModo(value); setErro(null); }}
      className={`flex-1 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${
        modo === value ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      {children}
    </button>
  );

  return (
    // Fase 3 - modal lateral (drawer) para autoria/envio da mensagem. Painel
    // ml-auto (não Sheet Radix) porque é aberto de dentro do OperacaoModal, que
    // já é um Sheet - evita diálogos Radix aninhados.
    <div className="fixed inset-0 bg-black/40 flex z-[60]" onClick={onClose}>
      <div
        className="relative ml-auto h-full w-full sm:max-w-md bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-slate-100">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-slate-900">Enviar mensagem de WhatsApp</h3>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              Para <span className="font-medium text-slate-700">{nomeComprador || '-'}</span>
              {principal?.telefone ? ` · ${principal.telefone}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 text-slate-400 hover:text-slate-600" aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {semTelefone && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>Proponente principal sem telefone cadastrado - cadastre um contato de telefone/WhatsApp na aba Resumo para habilitar o envio.</span>
            </p>
          )}

          {/* Seletor de modo */}
          <div className="flex gap-2">
            <TabBtn value="template">Usar template</TabBtn>
            <TabBtn value="novo">Novo template</TabBtn>
            <TabBtn value="custom">Personalizada</TabBtn>
          </div>

          {/* Conteúdo por modo */}
          {modo === 'template' && (
            <div className="space-y-2">
              {carregandoTpl ? (
                <p className="text-sm text-slate-400">Carregando templates…</p>
              ) : templates.length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum template salvo. Use “Novo template” ou “Personalizada”.</p>
              ) : (
                <select
                  value={selId}
                  onChange={(e) => setSelId(e.target.value)}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2"
                >
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              )}
            </div>
          )}

          {modo === 'novo' && (
            <div className="space-y-2">
              <input
                value={novoNome}
                onChange={(e) => setNovoNome(e.target.value)}
                placeholder="Nome do template (ex.: Análise de crédito)"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2"
              />
              <textarea
                rows={5}
                value={novoCorpo}
                onChange={(e) => setNovoCorpo(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 font-mono"
              />
              <p className="text-[11px] text-slate-400">Variáveis: <code>{'{{nome}}'}</code> (comprador) · <code>{'{{link}}'}</code> (formulário de crédito)</p>
            </div>
          )}

          {modo === 'custom' && (
            <div className="space-y-2">
              <textarea
                rows={6}
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2"
              />
              <p className="text-[11px] text-slate-400">Variáveis: <code>{'{{nome}}'}</code> · <code>{'{{link}}'}</code> - esta mensagem não é salva.</p>
            </div>
          )}

          {/* Pré-visualização */}
          {preview && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-[11px] font-semibold text-emerald-800 mb-1">Pré-visualização</p>
              <p className="text-xs text-emerald-900 whitespace-pre-wrap">{preview}</p>
            </div>
          )}

          {erro && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{erro}</p>}
        </div>

        {/* Footer */}
        <div className="shrink-0 flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100 bg-white">
          <button type="button" onClick={onClose} className="text-xs font-medium text-slate-500 hover:text-slate-700 px-3 py-2">
            Cancelar
          </button>
          <button
            type="button"
            onClick={enviar}
            disabled={semTelefone || salvando}
            className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-colors"
          >
            {salvando ? 'Salvando…' : modo === 'novo' ? 'Salvar e enviar' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}
